/**
 * Join Community Modal
 * 
 * Displays a step-by-step progress modal when joining a community.
 * Similar to the funding flow modal, shows each step status:
 * - Connect Wallet
 * - Switch Network
 * - Check Balance
 * - Approve Token (ERC20 only)
 * - Send Payment
 * - Mint NFT
 * - Confirm Membership
 * 
 * @module components/communities/JoinCommunityModal
 */

import React from "react";
import { BsCheckCircleFill, BsXCircleFill, BsArrowRepeat, BsWallet2, BsHddNetwork, BsCashStack, BsShieldCheck, BsCreditCard, BsAward, BsCheckAll } from "react-icons/bs";
import { Oval } from "react-loader-spinner";

/**
 * Step icon mapping
 */
const stepIcons = {
  connectWallet: BsWallet2,
  switchNetwork: BsHddNetwork,
  checkBalance: BsCashStack,
  checkEligibility: BsShieldCheck,
  approveToken: BsShieldCheck,
  mintAndPay: BsAward,
  confirm: BsCheckAll,
};

/**
 * Status icon component
 */
const StatusIcon = ({ status, stepKey }) => {
  const Icon = stepIcons[stepKey] || BsCheckCircleFill;

  switch (status) {
    case "success":
      return <BsCheckCircleFill className="text-green-500 text-xl" />;
    case "error":
      return <BsXCircleFill className="text-red-500 text-xl" />;
    case "loading":
      return (
        <Oval
          height={20}
          width={20}
          color="#8538ff"
          secondaryColor="#8538ff"
          strokeWidth={4}
          visible={true}
        />
      );
    default:
      return <Icon className="text-gray-400 text-xl" />;
  }
};

/**
 * Single step row component
 */
const StepRow = ({ stepKey, step, isActive }) => {
  if (step.hidden) return null;

  const statusColors = {
    idle: "text-gray-400",
    loading: "text-primary",
    success: "text-green-600",
    error: "text-red-600",
  };

  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
        isActive ? "bg-purple-50 border-2 border-primary" : "bg-gray-50"
      } ${step.status === "success" ? "opacity-75" : ""}`}
    >
      <StatusIcon status={step.status} stepKey={stepKey} />
      <div className="flex-1">
        <p className={`font-medium ${statusColors[step.status]}`}>
          {step.label}
        </p>
        {step.status === "loading" && (
          <p className="text-xs text-gray-500 mt-1">Processing...</p>
        )}
        {step.status === "error" && (
          <p className="text-xs text-red-500 mt-1">Failed - please try again</p>
        )}
      </div>
      {step.status === "success" && (
        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
          Done
        </span>
      )}
    </div>
  );
};

/**
 * Progress bar component
 */
const ProgressBar = ({ progress }) => (
  <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
    <div
      className="bg-gradient-to-r from-purple-600 to-blue-500 h-2 rounded-full transition-all duration-500"
      style={{ width: `${progress}%` }}
    />
  </div>
);

/**
 * Main modal component
 */
const JoinCommunityModal = ({
  isOpen,
  onClose,
  community,
  joinStatus,
  progress,
  currentStep,
  error,
  txHash,
  isJoining,
  onRetry,
}) => {
  if (!isOpen) return null;

  const isComplete = progress === 100;
  const hasError = error || Object.values(joinStatus).some(s => s.status === "error");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
          <h2 className="text-xl font-bold mb-1">
            {isComplete
              ? "Welcome to the Community! 🎉"
              : hasError
              ? "Something went wrong"
              : "Joining Community..."}
          </h2>
          <p className="text-sm text-white/80">
            {community?.name}
          </p>
        </div>

        {/* Progress */}
        <div className="p-6">
          <ProgressBar progress={progress} />

          {/* Steps */}
          <div className="space-y-3">
            {Object.entries(joinStatus).map(([key, step]) => (
              <StepRow
                key={key}
                stepKey={key}
                step={step}
                isActive={currentStep === key}
              />
            ))}
          </div>

          {/* Error message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Transaction hash */}
          {txHash && (
            <div className="mt-4 p-4 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-1">Transaction Hash:</p>
              <p className="text-xs font-mono text-gray-700 break-all">
                {txHash}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            {isComplete ? (
              <button
                onClick={onClose}
                className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
              >
                View Community
              </button>
            ) : hasError ? (
              <>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onRetry}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  <BsArrowRepeat />
                  Try Again
                </button>
              </>
            ) : (
              <button
                disabled
                className="flex-1 py-3 bg-gray-200 text-gray-500 rounded-xl font-medium cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Oval height={16} width={16} color="#666" secondaryColor="#999" />
                Processing...
              </button>
            )}
          </div>

          {/* Disclaimer */}
          {!isComplete && !hasError && (
            <p className="text-xs text-gray-500 text-center mt-4">
              Please keep this window open and confirm transactions in your wallet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinCommunityModal;
